import './playerView.css';

import { useAuthContext } from '../../../common/contexts/useAuthContext';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../recoil/atoms/userConfigState';
import { tenantScopesState } from '../../recoil/atoms/tenantScopesState';
import { selectedPlayerState } from '../../recoil/atoms/selectedPlayerState';
import { playerCareersState } from '../../recoil/atoms/playerCareersState';
import { competitionsState } from '../../recoil/atoms/competitionsState';
import { playersState } from '../../recoil/atoms/playersState';
import { useSetSelectedPlayer } from '../../recoil/hooks/useSetSelectedPlayer';
import { useOpenGlobalModal } from '../../recoil/hooks/useOpenGlobalModal';

import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import BlockIcon from '@mui/icons-material/Block';

import { translate } from '../../../common/language/translations';
import { getTenantColor, getTenantContrastColor } from '../../static/tenantConfigs';
import { SelectPlayerView } from '../searchPlayers/SelectPlayerView';
import { PlayerIterationDropDown } from '../controls/dropDowns/PlayerIterationDropDown';
import { useGetAndAddPlayerCareer } from '../../recoil/hooks/useGetAndAddPlayerCareer';
import { CompareRadarPlot } from '../plots/CompareRadarPlot';
import { playerMetricMap } from '../../static/playerMetrics';
import { getIndexColor } from '../../utils/colorUtils';
import { IconButton } from '../controls/buttons/IconButton';
import { ProgressCircle } from '../display/visualization/ProgressCircle';
import { PlayerImage } from '../display/images/PlayerImage';
import { ClubLogo } from '../display/images/ClubLogo';
import { CountryFlag } from '../display/images/CountryFlag';
import { dateStringToTextFormat } from '../../utils/dateUtils';
import { IndexBar } from '../display/visualization/IndexBar';
import { SkillcornerToggle } from '../controls/input/SkillcornerToggle';
import { PlayerCareer, PlayerClubIteration, PlayerOverview } from '../../types/player';
import { PositionData, PositionGroupKey } from '../../types/position';
import { StringToAnyMap } from '../../types/common';
import { getCurrentCompetitionName, getDisplayPlayerName } from '../../utils/nameUtils';
import { getIterationsAndSelectedClubIterationId, getPlayerPropertyValue } from '../../utils/playerUtils';
import { getAge } from '../../utils/ageUtils';
import { getDisplayPositions } from '../../utils/positionUtils';
import { getRoundedIndexValue } from '../../utils/metricUtils';


interface PlayerViewCompareProps {
  iterationsWithEventData: PlayerClubIteration[] | undefined;
}


export const PlayerViewCompare: React.FC<PlayerViewCompareProps> = ({
  iterationsWithEventData,
}) => {

  const { currentUser } = useAuthContext();

  const userConfig = useRecoilValue(userConfigState);
  const tenantScopes = useRecoilValue(tenantScopesState);
  const competitions = useRecoilValue(competitionsState);
  const players = useRecoilValue(playersState);
  const playerCareers = useRecoilValue(playerCareersState);
  const getAndAddPlayerCareer = useGetAndAddPlayerCareer();

  const selectedPlayer = useRecoilValue(selectedPlayerState);
  const { updateSelectedPlayer } = useSetSelectedPlayer();
  const isGoalkeeper = selectedPlayer?.playerOverview?.position_info?.primary_positions?.includes('GK') ?? false;

  const { openTextModal } = useOpenGlobalModal();
  const handleOpenMetricModal = (metric: string) => openTextModal(
    playerMetricMap[metric].name[userConfig?.language ?? 'en'],
    playerMetricMap[metric].description[userConfig?.language ?? 'en'],
    { infoType: 'metric', titleKey: metric }
  );

  const [physicalRadarToggleStatus, setPhysicalRadarToggleStatus] = useState<'blocked' | 'locked' | 'available' | undefined>();

  const [isIterationDropDownExpanded, setIsIterationDropDownExpanded] = useState<boolean>(false);
  const [isOtherIterationDropDownExpanded, setIsOtherIterationDropDownExpanded] = useState<boolean>(false);
  const removeDropDownExpansions = () => {
    setIsIterationDropDownExpanded(false);
    setIsOtherIterationDropDownExpanded(false);
  };


  // selected player
  const [selectedClubIteration, setSelectedClubIteration] = useState<PlayerClubIteration | undefined>(undefined);

  const positionGroupKeysOfSelectedIteration: PositionGroupKey[] = useMemo(() => {
    if (!selectedClubIteration) return [];
    return selectedClubIteration?.position_groups.length === 1
      ? selectedClubIteration.position_groups
      : ['OVERALL', ...selectedClubIteration.position_groups];
  }, [selectedClubIteration]);

  const selectedPositionGroupKey: PositionGroupKey = useMemo(() => {
    if (
      selectedPlayer?.selectedClubIterationPositionGroup &&
      positionGroupKeysOfSelectedIteration.includes(selectedPlayer.selectedClubIterationPositionGroup)
    ) return selectedPlayer.selectedClubIterationPositionGroup;

    if (positionGroupKeysOfSelectedIteration.length === 1) return positionGroupKeysOfSelectedIteration[0];

    return 'OVERALL';
  }, [positionGroupKeysOfSelectedIteration, selectedPlayer?.selectedClubIterationPositionGroup]);


  // other player
  const otherPlayerOverview = selectedPlayer?.playerToCompareAgainst;
  const otherPlayerDocument = otherPlayerOverview && players ? players[otherPlayerOverview.id] : undefined;
  const [showOtherPlayerConfidence, setShowOtherPlayerConfidence] = useState<boolean>(false);
  const [otherPlayerClubIterationArray, setOtherPlayerClubIterationArray] = useState<PlayerClubIteration[] | undefined>(undefined);

  const [otherSelectedClubIteration, setOtherSelectedClubIteration] = useState<PlayerClubIteration | undefined>(undefined);

  const otherPositionGroupKeysOfSelectedIteration: PositionGroupKey[] = useMemo(() => {
    if (!otherSelectedClubIteration) return [];
    return otherSelectedClubIteration?.position_groups.length === 1
      ? otherSelectedClubIteration.position_groups
      : ['OVERALL', ...otherSelectedClubIteration.position_groups];
  }, [otherSelectedClubIteration]);

  const otherSelectedPositionGroupKey: PositionGroupKey = useMemo(() => {
    if (selectedPlayer?.otherSelectedClubIterationPositionGroup) return selectedPlayer.otherSelectedClubIterationPositionGroup;
    if (otherSelectedClubIteration && otherSelectedClubIteration.position_groups.length === 1) return otherSelectedClubIteration.position_groups[0];
    return 'OVERALL';
  }, [otherSelectedClubIteration, selectedPlayer?.otherSelectedClubIterationPositionGroup]);


  const handleSelectOtherPlayer = (player: StringToAnyMap) => {
    if (!player.event_data_available) return;

    const otherPlayerIsGoalkeeper = player.position_info?.primary_positions?.includes('GK') ?? false;
    if (isGoalkeeper !== otherPlayerIsGoalkeeper) return;

    updateSelectedPlayer(selectedPlayer, { playerToCompareAgainst: player as PlayerOverview });
  };


  const handleOtherPlayerCloseClick = () => {
    updateSelectedPlayer(selectedPlayer, { playerToCompareAgainst: undefined });
    setOtherPlayerClubIterationArray([]);
  };


  useEffect(() => {
    if (otherPlayerOverview && playerCareers) {

      const fetchAndSetOtherPlayerCareer = async () => {
        let otherPlayerCareer: PlayerCareer | undefined = playerCareers[otherPlayerOverview.id];
        if (!otherPlayerCareer) {
          otherPlayerCareer = await getAndAddPlayerCareer(otherPlayerOverview.id, currentUser);
        }

        if (otherPlayerCareer) {
          const clubIterationArray: PlayerClubIteration[] = [];

          Object.keys(otherPlayerCareer).forEach(clubIterationId => {
            if (otherPlayerCareer[clubIterationId].event_data_exist) {
              clubIterationArray.push({ ...otherPlayerCareer[clubIterationId], club_iteration_id: clubIterationId });
            }
          });

          clubIterationArray.sort(
            (a, b) => b.basic_stats?.plot_date && a.basic_stats?.plot_date
              ? b.basic_stats.plot_date.localeCompare(a.basic_stats.plot_date)
              : 0
          );

          const { newIterations, newSelectedClubIterationId } = getIterationsAndSelectedClubIterationId(
            clubIterationArray,
            competitions,
          );

          updateSelectedPlayer(selectedPlayer, { otherSelectedClubIterationId: newSelectedClubIterationId, otherSelectedClubIterationPositionGroup: undefined });
          setOtherPlayerClubIterationArray(newIterations);
        }
      };

      fetchAndSetOtherPlayerCareer();
    }
  }, [currentUser, otherPlayerOverview, playerCareers]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (iterationsWithEventData) {
      if (selectedPlayer?.selectedClubIterationId) {
        const clubIteration = iterationsWithEventData.find(iteration => iteration.club_iteration_id === selectedPlayer.selectedClubIterationId);
        setSelectedClubIteration(clubIteration);
        if (!clubIteration?.physical_data_available) {
          updateSelectedPlayer(selectedPlayer, { isPhysicalCompareRadarPlotToggled: false });
        }
      }

      else {
        const { newSelectedClubIterationId } = getIterationsAndSelectedClubIterationId(
          iterationsWithEventData ?? [],
          competitions,
        );
        updateSelectedPlayer(selectedPlayer, { selectedClubIterationId: newSelectedClubIterationId, selectedClubIterationPositionGroup: undefined });
      }
    }
  }, [selectedPlayer?.selectedClubIterationId, iterationsWithEventData]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (selectedPlayer?.otherSelectedClubIterationId && otherPlayerClubIterationArray) {
      const clubIteration = otherPlayerClubIterationArray.find(iteration => iteration.club_iteration_id === selectedPlayer.otherSelectedClubIterationId);
      setOtherSelectedClubIteration(clubIteration);
      if (!clubIteration?.physical_data_available) {
        updateSelectedPlayer(selectedPlayer, { isPhysicalCompareRadarPlotToggled: false });
      }
    }
    else {
      setOtherSelectedClubIteration(undefined);
    }
  }, [selectedPlayer?.otherSelectedClubIterationId, otherPlayerClubIterationArray]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    let toggleStatus: 'blocked' | 'locked' | 'available' = 'available';

    // if either one of the selected iterations does not have physical data at all, the physical radar is blocked
    if (
      (selectedClubIteration && !selectedClubIteration.physical_data_exist) ||
      (otherSelectedClubIteration && !otherSelectedClubIteration.physical_data_exist)
    ) {
      toggleStatus = 'blocked';
    }

    // if either one of the selected iterations has physical data that is not available, the physical radar is locked
    else if (
      (selectedClubIteration && selectedClubIteration.physical_data_exist && !selectedClubIteration.physical_data_available) ||
      (otherSelectedClubIteration && otherSelectedClubIteration.physical_data_exist && !otherSelectedClubIteration.physical_data_available)
    ) {
      toggleStatus = 'locked';
    }

    if (toggleStatus !== 'available') {
      updateSelectedPlayer(selectedPlayer, { isPhysicalRadarPlotToggled: false });
    }

    setPhysicalRadarToggleStatus(toggleStatus);
  }, [selectedClubIteration, otherSelectedClubIteration]); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    if (otherPlayerOverview) {
      const timer = setTimeout(() => {
        setShowOtherPlayerConfidence(true);
      }, 200);

      return () => clearTimeout(timer);
    }
    else {
      setShowOtherPlayerConfidence(false);
    }
  }, [otherPlayerOverview]);


  const minutesPlayedIndex = selectedClubIteration
    ? Math.min(
      (selectedClubIteration?.position_group_stats?.[selectedPositionGroupKey]?.minutes_played ?? 0) / 150,
      10
    ) : 0;

  const otherMinutesPlayedIndex = otherSelectedClubIteration
    ? Math.min(
      (otherSelectedClubIteration?.position_group_stats?.[otherSelectedPositionGroupKey]?.minutes_played ?? 0) / 150,
      10
    ) : 0;

  const otherPlayerSkillIndex = getRoundedIndexValue(otherPlayerOverview?.position_group_stats?.OVERALL?.skill_index?.index);
  const otherPlayerSkillIndexConfidence = otherPlayerOverview?.position_group_stats?.OVERALL?.skill_index?.confidence ?? 0;

  const otherPlayerPhysicalIndex = getRoundedIndexValue(otherPlayerOverview?.position_group_stats?.OVERALL?.physical_index?.index);
  const otherPlayerPhysicalIndexConfidence = otherPlayerOverview?.position_group_stats?.OVERALL?.physical_index?.confidence ?? 0;
  const otherPlayerPhysicalIndexPossibleConfidence = otherPlayerOverview?.position_group_stats?.OVERALL?.physical_index?.possible_confidence ?? 0;


  const tenantColor = getTenantColor(userConfig?.club ?? '');
  const tenantContrastColor = getTenantContrastColor(userConfig?.club ?? '');


  return (
    <div className='full-size-container'>

      {(isIterationDropDownExpanded || isOtherIterationDropDownExpanded) &&
        <div className='filter-section-empty-background' onClick={removeDropDownExpansions} />
      }

      <div className='player-view-widget-container player-view-compare-iteration-section'>

        <div
          className='player-view-compare-iteration-section-line'
          style={{ backgroundColor: tenantColor }}
        />

        <div
          className='player-view-compare-iteration-section-line player-view-compare-iteration-section-line-other-player'
          style={{ backgroundColor: tenantContrastColor }}
        />

        <div className='player-view-compare-iteration-player-section'>
          <div className='player-view-compare-iteration-player-section-title'>
            {selectedPlayer ? getDisplayPlayerName(selectedPlayer.name, 20) : ''}
          </div>

          <div className='player-view-compare-iteration-drop-down'>
            <PlayerIterationDropDown
              id={'player-view-compare-iteration-drop-down'}
              dropDownOptions={iterationsWithEventData ?? []}
              selectedClubIteration={selectedClubIteration}
              handleIterationClick={(value: string) =>
                updateSelectedPlayer(selectedPlayer, { selectedClubIterationId: value, selectedClubIterationPositionGroup: undefined })
              }
              isDropDownExpanded={isIterationDropDownExpanded}
              setIsDropDownExpanded={setIsIterationDropDownExpanded}
              defaultDropDownText={'selectSeason'}
              defaultDropDownTextColor={'#00000080'}
              marginBetweenOptions={3}
              maxHeight={'70vh'}
            />
          </div>

          <div className='player-view-compare-positions-groups-row'>
            {selectedClubIteration && positionGroupKeysOfSelectedIteration.map(positionGroup => {

              const isSelected = selectedPositionGroupKey === positionGroup;
              const isMultiplePositionGroups = positionGroupKeysOfSelectedIteration.length > 1;
              const positions: PositionData[] = selectedClubIteration.position_group_stats?.[positionGroup]?.position_info?.positions ?? [];

              return (
                <div
                  key={positionGroup + selectedClubIteration.club_iteration_id}
                  className={
                    'player-view-compare-position-group'
                    + (isSelected ? ' player-view-compare-position-group-selected' : '')
                    + (isMultiplePositionGroups ? ' player-view-compare-position-group-hover' : '')
                  }
                  style={{
                    border: '1px solid ' + (isSelected ? tenantColor : 'transparent'),
                    fontSize: positions.length > 2 ? 11 : 12
                  }}
                  onClick={
                    () => isMultiplePositionGroups
                      ? updateSelectedPlayer(selectedPlayer, { selectedClubIterationPositionGroup: isSelected ? 'OVERALL' : positionGroup })
                      : undefined
                  }
                >
                  {positionGroup === 'OVERALL' && (
                    <div>
                      {translate('all', userConfig?.language)}
                    </div>
                  )}

                  {positionGroup !== 'OVERALL' && positions.map((position: PositionData) => {
                    return (
                      <div key={position.position}>
                        {translate(position.position, userConfig?.language)}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>

        </div>

        <div className='player-view-compare-iteration-section-divider' />

        {otherPlayerOverview && (
          <div className='player-view-compare-iteration-player-section'>
            <div className='player-view-compare-iteration-player-section-title-container-other-player'>
              <div className='player-view-compare-iteration-player-section-title-other-player'>
                {getDisplayPlayerName(otherPlayerOverview.name, 20)}
              </div>
            </div>

            <div className='player-view-compare-iteration-drop-down-other-player'>
              <PlayerIterationDropDown
                id={'player-view-compare-iteration-drop-down-other-player'}
                dropDownOptions={otherPlayerClubIterationArray ?? []}
                selectedClubIteration={otherSelectedClubIteration}
                handleIterationClick={(value: string) =>
                  updateSelectedPlayer(selectedPlayer, { otherSelectedClubIterationId: value, otherSelectedClubIterationPositionGroup: undefined })
                }
                isDropDownExpanded={isOtherIterationDropDownExpanded}
                setIsDropDownExpanded={setIsOtherIterationDropDownExpanded}
                defaultDropDownText={'selectSeason'}
                defaultDropDownTextColor={'#00000080'}
                marginBetweenOptions={3}
                maxHeight={'70vh'}
              />
            </div>

            <div className='player-view-compare-positions-groups-row'>
              {otherSelectedClubIteration && otherPositionGroupKeysOfSelectedIteration.map(positionGroup => {

                const isSelected = otherSelectedPositionGroupKey === positionGroup;
                const isMultiplePositionGroups = otherPositionGroupKeysOfSelectedIteration.length > 1;
                const positions: PositionData[] = otherSelectedClubIteration.position_group_stats?.[positionGroup]?.position_info?.positions ?? [];

                return (
                  <div
                    key={positionGroup + otherSelectedClubIteration.club_iteration_id}
                    className={
                      'player-view-compare-position-group'
                      + (isSelected ? ' player-view-compare-position-group-selected' : '')
                      + (isMultiplePositionGroups ? ' player-view-compare-position-group-hover' : '')
                    }
                    style={{
                      border: '1px solid ' + (isSelected ? tenantContrastColor : 'transparent'),
                      fontSize: positions.length > 2 ? 11 : 12
                    }}
                    onClick={
                      () => isMultiplePositionGroups
                        ? updateSelectedPlayer(selectedPlayer, { otherSelectedClubIterationPositionGroup: isSelected ? 'OVERALL' : positionGroup })
                        : undefined
                    }
                  >
                    {positionGroup === 'OVERALL' && (
                      <div>
                        {translate('all', userConfig?.language)}
                      </div>
                    )}

                    {positionGroup !== 'OVERALL' && positions.map((position: PositionData) => {
                      return (
                        <div key={position.position}>
                          {translate(position.position, userConfig?.language)}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {!otherPlayerOverview && (
          <div className='player-view-compare-iteration-player-section'>
            <div className='player-view-compare-iteration-player-section-no-player-selected'>
              {translate('noPlayerSelected', userConfig?.language)}
            </div>
          </div>
        )}
      </div>

      <div className='player-view-widget-container player-view-compare-data-section'>
        <div
          className='player-view-compare-iteration-section-line'
          style={{ backgroundColor: tenantColor }}
        />

        <div
          className='player-view-compare-iteration-section-line player-view-compare-iteration-section-line-other-player'
          style={{ backgroundColor: tenantContrastColor }}
        />

        {selectedClubIteration && selectedPositionGroupKey !== undefined && (
          <div className='player-view-compare-data-info-column'>
            <div className='player-view-compare-data-info-column-entry'>
              <div className='player-view-compare-data-info-column-label'>
                {translate('club', userConfig?.language)}
              </div>
              <div className='player-view-compare-data-info-column-value' style={{ marginTop: 4 }}>
                <ClubLogo
                  club={selectedClubIteration.club}
                  size={18}
                  showClubName={true}
                  showHyphenIfEmpty={true}
                  containerStyle={{ gap: 3 }}
                />
              </div>
            </div>
            <div className='player-view-compare-data-info-column-entry'>
              <div className='player-view-compare-data-info-column-label'>
                {translate('minutes', userConfig?.language)}
              </div>
              <div className='player-view-compare-data-info-column-value' style={{ marginTop: 4 }}>
                {selectedClubIteration.position_group_stats?.[selectedPositionGroupKey]?.minutes_played !== undefined
                  ? Math.round(selectedClubIteration.position_group_stats?.[selectedPositionGroupKey].minutes_played)
                  : '-'}

                <div
                  style={{
                    marginTop: -4,
                    marginLeft: 7,
                  }}
                >
                  <ProgressCircle
                    size={16}
                    progress={minutesPlayedIndex * 10}
                    color={getIndexColor(minutesPlayedIndex, 65)}
                    strokeWidth={9}
                    trailColor='#ffffff55'
                  />
                </div>
              </div>
            </div>
            <div className='player-view-compare-data-info-column-entry'>
              <div className='player-view-compare-data-info-column-label'>
                <div
                  className='player-view-metric-label'
                  title={translate('showDescription', userConfig?.language)}
                  onClick={() => handleOpenMetricModal('skill_rating')}
                >
                  {translate('skillRating', userConfig?.language)}
                </div>
              </div>
              <div className='player-view-compare-data-info-column-value' style={{ fontSize: 16, fontWeight: 500, marginTop: 3 }}>
                {selectedClubIteration.position_group_stats?.[selectedPositionGroupKey]?.skill_rating !== undefined
                  ? Math.round(selectedClubIteration.position_group_stats?.[selectedPositionGroupKey].skill_rating * 10) / 10
                  : '-'}
              </div>
            </div>
            <div className='player-view-compare-data-info-column-entry'>
              <div className='player-view-compare-data-info-column-label'>
                {translate('goals', userConfig?.language)}
              </div>
              <div className='player-view-compare-data-info-column-value'>
                {selectedClubIteration.position_group_stats?.[selectedPositionGroupKey]?.metrics?.n_goals ?? '-'}
              </div>
            </div>
            <div className='player-view-compare-data-info-column-entry'>
              <div className='player-view-compare-data-info-column-label'>
                {translate('assists', userConfig?.language)}
              </div>
              <div className='player-view-compare-data-info-column-value'>
                {selectedClubIteration.position_group_stats?.[selectedPositionGroupKey]?.metrics?.n_assists ?? '-'}
              </div>
            </div>
          </div>
        )}

        <div className='player-view-compare-radar-section'>
          {!isGoalkeeper && tenantScopes?.hasSkillcorner && physicalRadarToggleStatus !== undefined && (
            <div className='player-view-compare-radar-plot-toggle-container'>
              <SkillcornerToggle
                isToggled={selectedPlayer?.isPhysicalCompareRadarPlotToggled ?? false}
                language={userConfig?.language ?? 'en'}
                setIsToggled={physicalRadarToggleStatus === 'available'
                  ? (value: boolean) => updateSelectedPlayer(selectedPlayer, { isPhysicalCompareRadarPlotToggled: value })
                  : undefined
                }
                toggleStatus={physicalRadarToggleStatus}
              />
            </div>
          )}

          {userConfig && (
            <div
              className='full-size'
              style={{
                paddingTop: tenantScopes?.hasSkillcorner && !isGoalkeeper ? 10 : undefined,
              }}
            >
              <CompareRadarPlot
                selectedClubIteration={selectedClubIteration}
                selectedPositionGroupKey={selectedPositionGroupKey}
                tenantColor={tenantColor}

                otherSelectedClubIteration={otherSelectedClubIteration}
                otherSelectedPositionGroupKey={otherSelectedPositionGroupKey}
                tenantContrastColor={tenantContrastColor}

                isPhysicalRadarToggled={selectedPlayer?.isPhysicalCompareRadarPlotToggled ?? false}
                isGoalkeeper={isGoalkeeper}
                language={userConfig.language}
              />
            </div>
          )}
        </div>

        {otherSelectedClubIteration && otherSelectedPositionGroupKey !== undefined && (
          <div className='player-view-compare-data-info-column player-view-compare-data-info-column-other-player'>
            <div className='player-view-compare-data-info-column-entry-other-player'>
              <div className='player-view-compare-data-info-column-label player-view-compare-data-info-column-label-other-player'>
                {translate('club', userConfig?.language)}
              </div>
              <div className='player-view-compare-data-info-column-value player-view-compare-data-info-column-value-other-player' style={{ marginTop: 4 }}>
                <ClubLogo
                  club={otherSelectedClubIteration.club}
                  size={18}
                  showClubName={true}
                  showHyphenIfEmpty={true}
                  containerStyle={{ gap: 3 }}
                />
              </div>
            </div>
            <div className='player-view-compare-data-info-column-entry-other-player'>
              <div className='player-view-compare-data-info-column-label player-view-compare-data-info-column-label-other-player'>
                {translate('minutes', userConfig?.language)}
              </div>
              <div className='player-view-compare-data-info-column-value player-view-compare-data-info-column-value-other-player' style={{ marginTop: 4 }}>
                <div
                  style={{
                    marginTop: -4,
                    marginRight: 7,
                  }}
                >
                  <ProgressCircle
                    size={16}
                    progress={otherMinutesPlayedIndex * 10}
                    color={getIndexColor(otherMinutesPlayedIndex, 65)}
                    strokeWidth={9}
                    trailColor='#ffffff55'
                  />
                </div>
                {otherSelectedClubIteration.position_group_stats?.[otherSelectedPositionGroupKey]?.minutes_played !== undefined
                  ? Math.round(otherSelectedClubIteration.position_group_stats?.[otherSelectedPositionGroupKey].minutes_played)
                  : '-'}
              </div>
            </div>
            <div className='player-view-compare-data-info-column-entry-other-player'>
              <div className='player-view-compare-data-info-column-label player-view-compare-data-skill-rating-label-other-player'>
                <div
                  className='player-view-metric-label'
                  title={translate('showDescription', userConfig?.language)}
                  onClick={() => handleOpenMetricModal('skill_rating')}
                >
                  {translate('skillRating', userConfig?.language)}
                </div>
              </div>
              <div
                className='player-view-compare-data-info-column-value player-view-compare-data-info-column-value-other-player'
                style={{ fontSize: 16, fontWeight: 500, marginTop: 3 }}
              >
                {otherSelectedClubIteration.position_group_stats?.[otherSelectedPositionGroupKey]?.skill_rating !== undefined
                  ? Math.round(otherSelectedClubIteration.position_group_stats?.[otherSelectedPositionGroupKey].skill_rating * 10) / 10
                  : '-'}
              </div>
            </div>
            <div className='player-view-compare-data-info-column-entry-other-player'>
              <div className='player-view-compare-data-info-column-label player-view-compare-data-info-column-label-other-player'>
                {translate('goals', userConfig?.language)}
              </div>
              <div className='player-view-compare-data-info-column-value player-view-compare-data-info-column-value-other-player'>
                {otherSelectedClubIteration.position_group_stats?.[otherSelectedPositionGroupKey]?.metrics?.n_goals ?? '-'}
              </div>
            </div>
            <div className='player-view-compare-data-info-column-entry-other-player'>
              <div className='player-view-compare-data-info-column-label player-view-compare-data-info-column-label-other-player'>
                {translate('assists', userConfig?.language)}
              </div>
              <div className='player-view-compare-data-info-column-value player-view-compare-data-info-column-value-other-player'>
                {otherSelectedClubIteration.position_group_stats?.[otherSelectedPositionGroupKey]?.metrics?.n_assists ?? '-'}
              </div>
            </div>
          </div>
        )}


      </div>

      <div className='player-view-widget-container player-view-compare-other-player-section'>

        {!otherPlayerOverview && (
          <div>
            <SelectPlayerView
              onPlayerClick={handleSelectOtherPlayer}
              titleKey={'selectPlayerForComparison'}
              maxTableHeight={'62vh'}
              isGoalkeeper={isGoalkeeper}
              disablePlayersWithoutEventData={true}
            />
          </div>
        )}

        {otherPlayerOverview && (
          <div>

            <IconButton
              onClick={handleOtherPlayerCloseClick}
              icon={<CloseIcon style={{ fontSize: 26 }} />}
              size={30}
              style={{ position: 'absolute', top: 16, right: 16 }}
              startWhite={true}
            />

            <PlayerImage
              src={otherPlayerOverview.image_url}
              isSingleImage={true}
              containerStyle={{ margin: 'auto', marginTop: '3vh', width: '13vh', height: 'auto', minWidth: 80 }}
              imageStyle={{ width: '80%', height: 'auto' }}
            />

            <div className='player-view-compare-other-name'>
              {otherPlayerOverview.name}
            </div>

            {(!tenantScopes?.hasSkillcorner || isGoalkeeper) && (
              <div className='player-view-compare-other-player-rating-section'>

                <div className='player-view-compare-other-player-rating-entry'>
                  <div
                    className='player-view-compare-other-player-rating-label player-view-metric-label'
                    title={translate('showDescription', userConfig?.language)}
                    onClick={() => handleOpenMetricModal('skill_index')}
                  >
                    {translate('skillIndex', userConfig?.language)}
                  </div>

                  <div
                    className='player-view-compare-other-player-rating-value flex-column'
                    style={{
                      color: getIndexColor(otherPlayerSkillIndex),
                      fontSize: 19
                    }}
                  >
                    {otherPlayerSkillIndex}

                    <div className='flex-row align-center justify-center' style={{ marginTop: 6 }}>
                      <IndexBar
                        index={showOtherPlayerConfidence ? otherPlayerSkillIndex : 0}
                        confidence={otherPlayerSkillIndexConfidence}
                        color={'#c2d3e6'}
                        maxWidth={60}
                        height={2}
                        secondDimension={'opacity'}
                        trailColor={'#50566b'}
                        duration={0.4}
                      />
                    </div>
                  </div>
                </div>

                <div className='player-view-compare-other-player-rating-entry'>
                  <div
                    className='player-view-compare-other-player-rating-label player-view-metric-label'
                    title={translate('showDescription', userConfig?.language)}
                    onClick={() => handleOpenMetricModal('confidence')}
                  >
                    {translate('confidence', userConfig?.language)}
                  </div>
                  <div className='player-view-compare-other-player-rating-value'>
                    <ProgressCircle
                      size={30}
                      progress={showOtherPlayerConfidence ? otherPlayerSkillIndexConfidence * 100 : 0}
                      color={getIndexColor(otherPlayerSkillIndexConfidence * 10, 65)}
                      value={Math.round(otherPlayerSkillIndexConfidence * 100)}
                      fontSize={13}
                      duration={0.6}
                    />
                  </div>
                </div>

              </div>
            )}

            {tenantScopes?.hasSkillcorner && !isGoalkeeper && (
              <div className='player-view-compare-other-player-rating-section'>

                <div className='player-view-compare-other-player-rating-entry'>
                  <div
                    className='player-view-compare-other-player-rating-label player-view-metric-label'
                    title={translate('showDescription', userConfig?.language)}
                    onClick={() => handleOpenMetricModal('skill_index')}
                  >
                    {translate('skillIndex', userConfig?.language)}
                  </div>

                  <div className='player-view-compare-other-player-rating-value'>
                    <div style={{ marginRight: 10 }}>
                      <ProgressCircle
                        size={21}
                        progress={showOtherPlayerConfidence ? otherPlayerSkillIndexConfidence * 100 : 0}
                        color={getIndexColor(otherPlayerSkillIndexConfidence * 10, 65)}
                        value={Math.round(otherPlayerSkillIndexConfidence * 100)}
                        fontSize={10}
                        duration={0.6}
                      />
                    </div>

                    <div
                      className='player-view-compare-other-player-rating-value flex-column'
                      style={{
                        color: getIndexColor(otherPlayerSkillIndex, 60),
                        fontSize: 19
                      }}
                    >
                      {otherPlayerSkillIndex}

                      <div className='flex-row align-center justify-center' style={{ marginTop: 6 }}>
                        <IndexBar
                          index={showOtherPlayerConfidence ? otherPlayerSkillIndex : 0}
                          confidence={otherPlayerSkillIndexConfidence}
                          color={'#c2d3e6'}
                          maxWidth={50}
                          height={2}
                          secondDimension={'opacity'}
                          trailColor={'#50566b'}
                          duration={0.4}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className='player-view-compare-other-player-rating-entry'>
                  <div
                    className='player-view-compare-other-player-rating-label player-view-metric-label'
                    title={translate('showDescription', userConfig?.language)}
                    onClick={() => handleOpenMetricModal('physical_index')}
                  >
                    {translate('physicalIndex', userConfig?.language)}
                  </div>

                  {otherPlayerPhysicalIndexConfidence > 0 && (
                    <div className='player-view-compare-other-player-rating-value'>
                      <div
                        className='player-view-compare-other-player-rating-value flex-column'
                        style={{
                          color: getIndexColor(otherPlayerPhysicalIndex, 60),
                          fontSize: 19
                        }}
                      >
                        {otherPlayerPhysicalIndex}

                        <div className='flex-row align-center justify-center' style={{ marginTop: 6 }}>
                          <IndexBar
                            index={showOtherPlayerConfidence ? otherPlayerPhysicalIndex : 0}
                            confidence={otherPlayerPhysicalIndexConfidence}
                            color={'#c2d3e6'}
                            maxWidth={50}
                            height={2}
                            secondDimension={'opacity'}
                            trailColor={'#50566b'}
                            duration={0.4}
                          />
                        </div>
                      </div>

                      <div style={{ marginLeft: 10 }}>
                        <ProgressCircle
                          size={21}
                          progress={showOtherPlayerConfidence ? otherPlayerPhysicalIndexConfidence * 100 : 0}
                          color={getIndexColor(otherPlayerPhysicalIndexConfidence * 10, 60)}
                          value={Math.round(otherPlayerPhysicalIndexConfidence * 100)}
                          fontSize={10}
                          duration={0.6}
                        />
                      </div>
                    </div>
                  )}

                  {otherPlayerPhysicalIndexConfidence === 0 && otherPlayerPhysicalIndexPossibleConfidence > 0 && (
                    <div className='player-view-compare-other-player-rating-value'>
                      <LockIcon style={{ fontSize: 18, color: '#ffffffaa' }} />
                    </div>
                  )}

                  {otherPlayerPhysicalIndexConfidence === 0 && otherPlayerPhysicalIndexPossibleConfidence === 0 && (
                    <div className='player-view-compare-other-player-rating-value'>
                      <BlockIcon style={{ fontSize: 18, color: '#ffffffaa' }} />
                    </div>
                  )}
                </div>

              </div>
            )}

            <div className='player-view-compare-other-player-info-section'>
              <div className='player-view-compare-other-player-info-row'>
                <div className='player-view-compare-other-player-info-label'>
                  {translate('age', userConfig?.language)}
                </div>
                <div className='player-view-compare-other-player-info-value'>
                  {getPlayerPropertyValue('birth_date', otherPlayerDocument, otherPlayerOverview, undefined, getAge, '-')}
                </div>
              </div>

              <div className='player-view-compare-other-player-info-row'>
                <div className='player-view-compare-other-player-info-label'>
                  {translate('position', userConfig?.language)}
                </div>
                <div className='player-view-compare-other-player-info-value'>
                  {getDisplayPositions(otherPlayerOverview?.position_info?.primary_positions, userConfig?.language) ?? '-'}
                </div>
              </div>

              <div className='player-view-compare-other-player-info-row'>
                <div className='player-view-compare-other-player-info-label'>
                  {translate('nationality', userConfig?.language)}
                </div>
                <div className='player-view-compare-other-player-info-value'>
                  <CountryFlag
                    countryCode={getPlayerPropertyValue('country_code', otherPlayerDocument, otherPlayerOverview)}
                    width={16}
                    showCountryName={true}
                    showHyphenIfEmpty={true}
                    imageStyle={{ marginBottom: -1 }}
                  />
                </div>
              </div>

              <div className='player-view-compare-other-player-info-row'>
                <div className='player-view-compare-other-player-info-label'>
                  {translate('league', userConfig?.language)}
                </div>
                <div className='player-view-compare-other-player-info-value'>
                  {getCurrentCompetitionName(otherPlayerOverview.club, competitions)}
                </div>
              </div>

              <div className='player-view-compare-other-player-info-row'>
                <div className='player-view-compare-other-player-info-label'>
                  {translate('club', userConfig?.language)}
                </div>
                <div className='player-view-compare-other-player-info-value'>
                  <ClubLogo
                    club={otherPlayerOverview.club}
                    size={18}
                    showClubName={true}
                    showHyphenIfEmpty={true}
                    containerStyle={{ gap: 3 }}
                  />
                </div>
              </div>

              <div className='player-view-compare-other-player-info-row'>
                <div className='player-view-compare-other-player-info-label'>
                  {translate('height', userConfig?.language)}
                </div>
                <div className='player-view-compare-other-player-info-value'>
                  {getPlayerPropertyValue('height', otherPlayerDocument, otherPlayerOverview) ?? '-'}
                </div>
              </div>

              <div className='player-view-compare-other-player-info-row'>
                <div className='player-view-compare-other-player-info-label'>
                  {translate('contract', userConfig?.language)}
                </div>
                <div className='player-view-compare-other-player-info-value'>
                  {getPlayerPropertyValue('contract_expiration', otherPlayerDocument, otherPlayerOverview, undefined, dateStringToTextFormat, '-')}
                </div>
              </div>
            </div>

          </div>
        )}

      </div>
    </div>
  );
};
